import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Form, Spinner, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiGift, FiUpload } from 'react-icons/fi';
import { getAllPartners, importStandardCommercialGesturePayments } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import PartnerCompanyAutocomplete from '../../components/Common/PartnerCompanyAutocomplete';
import { useCommercialGesturePermissions } from './commercialGestures/CommercialGestureShared';
import './commercialGestures/commercialGestures.css';

const ImportResult = ({ data }) => {
    if (!data) return null;

    const updated = Array.isArray(data.updated_commercial_gestures) ? data.updated_commercial_gestures : [];
    const failed = Array.isArray(data.failed_rows) ? data.failed_rows : [];
    const updatedCount = data.summary?.updated ?? updated.length;
    const failedCount = data.summary?.failed ?? failed.length;
    const hasUpdates = updatedCount > 0;

    return (
        <div className="mt-3">
            <Alert variant={hasUpdates ? 'success' : failedCount > 0 ? 'warning' : 'info'}>
                <strong>
                    {data.message
                        || `${updatedCount} geste${updatedCount > 1 ? 's' : ''} commercial${updatedCount > 1 ? 's' : ''} mis à jour.`}
                </strong>
                <div className="small mt-1">
                    Mis à jour : {updatedCount} — Échecs : {failedCount}
                    {data.company_name_filter ? ` — Filtre partenaire : ${data.company_name_filter}` : ''}
                </div>
            </Alert>

            {updated.length > 0 && (
                <Card className="mb-3">
                    <Card.Header>
                        <strong>Gestes mis à jour ({updated.length})</strong>
                    </Card.Header>
                    <Table responsive size="sm" className="mb-0">
                        <thead>
                            <tr>
                                <th>Code police</th>
                                <th>Montant</th>
                                <th>Statut</th>
                                <th>E-mail client</th>
                                <th>E-mail planifié</th>
                            </tr>
                        </thead>
                        <tbody>
                            {updated.map((row) => (
                                <tr key={row.commercial_gesture_id || `${row.policy_code}-${row.client_email}`}>
                                    <td>{row.policy_code || '—'}</td>
                                    <td>{row.amount ? `${row.amount} EUR` : '—'}</td>
                                    <td>{row.status || '—'}</td>
                                    <td>{row.client_email || '—'}</td>
                                    <td>{row.email_scheduled ? 'Oui' : 'Non'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}

            {failed.length > 0 && (
                <Card className="mb-3">
                    <Card.Header>
                        <strong>Lignes rejetées ({failed.length})</strong>
                    </Card.Header>
                    <Table responsive size="sm" className="mb-0">
                        <thead>
                            <tr>
                                <th>Ligne</th>
                                <th>Code police</th>
                                <th>Raison</th>
                            </tr>
                        </thead>
                        <tbody>
                            {failed.map((row) => (
                                <tr key={`${row.row}-${row.policy_code}`}>
                                    <td>{row.row ?? '—'}</td>
                                    <td>{row.policy_code || '—'}</td>
                                    <td className="text-break">{row.error || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}
        </div>
    );
};

const AdminCommercialGestureBulkPayments = () => {
    const navigate = useNavigate();
    const { canWrite } = useCommercialGesturePermissions();
    const inFlightRef = useRef(false);
    const [file, setFile] = useState(null);
    const [fileKey, setFileKey] = useState(0);
    const [companyName, setCompanyName] = useState('');
    const [partners, setPartners] = useState([]);
    const [partnersLoading, setPartnersLoading] = useState(false);
    const [partnersError, setPartnersError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const loadPartners = async () => {
            setPartnersLoading(true);
            setPartnersError(null);
            try {
                const response = await getAllPartners();
                const list = response?.users && Array.isArray(response.users)
                    ? response.users
                    : (Array.isArray(response) ? response : []);
                if (!cancelled) setPartners(list);
            } catch (err) {
                if (!cancelled) {
                    setPartners([]);
                    setPartnersError(handleApiError(err, 'Erreur lors du chargement des partenaires'));
                }
            } finally {
                if (!cancelled) setPartnersLoading(false);
            }
        };
        loadPartners();
        return () => { cancelled = true; };
    }, []);

    const partnerOptions = useMemo(() => {
        const names = partners
            .map((partner) => partner.company_name?.trim())
            .filter(Boolean);
        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'fr'));
    }, [partners]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (inFlightRef.current) return;
        if (!file) {
            setError('Veuillez sélectionner un fichier Excel');
            return;
        }
        inFlightRef.current = true;
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const data = await importStandardCommercialGesturePayments(file, {
                company_name: companyName,
            });
            setResult(data);
            setFile(null);
            setFileKey((key) => key + 1);
        } catch (err) {
            setError(handleApiError(err, 'Erreur lors de l\'import des paiements'));
        } finally {
            inFlightRef.current = false;
            setLoading(false);
        }
    };

    if (!canWrite) {
        return (
            <div className="dashboard-page admin-commercial-gestures-page">
                <Alert variant="danger">Action non autorisée</Alert>
            </div>
        );
    }

    return (
        <div className="dashboard-page admin-commercial-gestures-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <span className="page-title-icon" aria-hidden="true">
                                    <FiGift />
                                </span>
                                Import des paiements — Gestes commerciaux
                            </h2>
                            <p className="text-muted mb-0">
                                Importez l&apos;Excel paid daily pour passer les gestes de « Lien RIB envoyé » à « Payé ».
                                Les sinistres et le statut de la police ne sont pas modifiés.
                            </p>
                        </div>
                        <Button
                            variant="outline-secondary"
                            onClick={() => navigate('/admin/trips/commercial-gestures')}
                        >
                            <FiArrowLeft className="me-2" />
                            Retour
                        </Button>
                    </div>

                    <Alert variant="light" className="border mb-3">
                        <p className="mb-2">
                            Fichier attendu : <code>import_paid_daily_standard_commercial_gestures_*.xlsx</code>
                        </p>
                        <p className="mb-2">
                            <strong>Colonnes requises :</strong>{' '}
                            <code>policy_code</code>, <code>reservation_number</code>,{' '}
                            <code>client_email</code>, <code>client_first_name</code>,{' '}
                            <code>client_last_name</code>, <code>claim_status</code>
                        </p>
                        <p className="mb-2">
                            <strong>Colonnes optionnelles :</strong> <code>iban</code>, <code>bic</code>
                        </p>
                        <p className="mb-0">
                            Seules les lignes <code>claim_status = Paid</code> sont traitées.
                            Un e-mail « Votre virement est en cours » peut être envoyé au client
                            (montant du geste), si l&apos;e-mail est activé pour le partenaire.
                        </p>
                    </Alert>

                    {loading && (
                        <Alert variant="info" className="mb-3">
                            <Spinner size="sm" className="me-2" animation="border" />
                            <strong>Import en cours...</strong> Cela peut prendre plusieurs minutes. Veuillez patienter.
                        </Alert>
                    )}

                    <Card className="mb-3">
                        <Card.Header>
                            <h5 className="mb-0">Importer un fichier Excel</h5>
                        </Card.Header>
                        <Card.Body>
                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Filtrer par partenaire (optionnel)</Form.Label>
                                    <PartnerCompanyAutocomplete
                                        value={companyName}
                                        onChange={setCompanyName}
                                        options={partnerOptions}
                                        loading={partnersLoading}
                                        error={partnersError}
                                        disabled={loading}
                                        placeholder="Tous les partenaires Standard"
                                    />
                                    <Form.Text className="text-muted">
                                        Sans filtre, tous les partenaires Standard du fichier sont traités.
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Fichier Excel <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        key={fileKey}
                                        type="file"
                                        accept=".xlsx,.xls"
                                        onChange={(e) => {
                                            const next = e.target.files?.[0] || null;
                                            setFile(next);
                                            setError(null);
                                        }}
                                        disabled={loading}
                                        required
                                    />
                                    <Form.Text className="text-muted d-block mt-2">
                                        Formats acceptés : <strong>.xlsx</strong>, <strong>.xls</strong>
                                    </Form.Text>
                                    {file && (
                                        <div className="mt-3 p-2 bg-light rounded">
                                            <small className="text-success">
                                                <strong>Fichier sélectionné :</strong> {file.name}
                                            </small>
                                        </div>
                                    )}
                                </Form.Group>

                                <Button variant="success" type="submit" disabled={loading || !file}>
                                    {loading ? (
                                        <>
                                            <Spinner size="sm" className="me-2" animation="border" />
                                            Import en cours...
                                        </>
                                    ) : (
                                        <>
                                            <FiUpload className="me-2" />
                                            Importer
                                        </>
                                    )}
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>

                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}
                    <ImportResult data={result} />
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminCommercialGestureBulkPayments;
